import { useState, useEffect } from 'react';
import { openDB } from 'idb';
import { OFFLINE_DATABASE_NAME } from '../dbConstants';

/**
 * Custom hook to check if specified IndexedDB stores are empty
 * 
 * @param storeNames - Array of store names to check
 * @param refreshTrigger - Optional value that when changed triggers a re-check
 * @returns Object containing loading state, empty status, and error information
 */
export const useOfflineStoresEmpty = (storeNames: string[], refreshTrigger?: any) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isEmpty, setIsEmpty] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [storeStatus, setStoreStatus] = useState<Record<string, boolean>>({});

  useEffect(() => {
    let isMounted = true;

    const checkStoresEmpty = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const db = await openDB(OFFLINE_DATABASE_NAME);
        const existingStoreNames = Array.from(db.objectStoreNames);
        
        const emptyStatuses: Record<string, boolean> = {};
        
        await Promise.all(
          storeNames.map(async (storeName) => {
            if (!existingStoreNames.includes(storeName)) {
              emptyStatuses[storeName] = true;
              return;
            }
            
            const transaction = db.transaction(storeName, 'readonly');
            const store = transaction.objectStore(storeName);
            const count = await store.count();
            emptyStatuses[storeName] = count === 0;
          })
        );
        
        if (isMounted) {
          setStoreStatus(emptyStatuses);
          
          const allEmpty = Object.values(emptyStatuses).every(isEmpty => isEmpty === true);
          setIsEmpty(allEmpty);
          setIsLoading(false);
        }
        
        db.close();
      } catch (err) {
        if (isMounted) {
          setError(err instanceof Error ? err : new Error('Unknown error checking stores'));
          setIsLoading(false);
        }
      }
    };

    checkStoresEmpty();

    return () => {
      isMounted = false;
    };
  }, [storeNames, refreshTrigger]);

  return { isLoading, isEmpty, error, storeStatus };
}; 